// Definições globais: registo aberto/fechado e modo de manutenção. Só admins editam; tudo vai para a auditoria.
import { useApi } from "@ecliptica/api-client/react";
import { contentLangs, useTranslation, type Lang } from "@ecliptica/i18n";
import { Alert, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Label, PageHeader, Skeleton, Switch, Textarea, toast } from "@ecliptica/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useCan } from "../../features/auth/RequireStaff";

/** Rótulo da mensagem de manutenção em cada língua. Só se editam as ativas; a das inativas grava-se como estava. */
const MESSAGE_KEY = { pt: "settings.messagePt", en: "settings.messageEn" } as const satisfies Record<Lang, string>;

export function SettingsPage() {
  const { t } = useTranslation();
  const api = useApi();
  const qc = useQueryClient();
  const can = useCan();
  const editable = can("settings.edit");
  const settings = useQuery({
    queryKey: ["settings"],
    queryFn: async () => {
      const res = await api.v1.admin.settings.$get();
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
  });

  const [registrationsOpen, setRegistrationsOpen] = useState(true);
  const [maintenance, setMaintenance] = useState({ on: false, message: { pt: "", en: "" } });
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (!settings.data) return;
    setRegistrationsOpen(settings.data.registrationsOpen);
    setMaintenance(settings.data.maintenance);
  }, [settings.data]);

  async function save(key: "registrationsOpen" | "maintenance", value: unknown) {
    const res = await api.v1.admin.settings[":key"].$put({ param: { key }, json: { value, reason: reason || undefined } });
    if (!res.ok) return toast.error(t("error.generic"));
    toast.success(t("settings.saved"));
    setReason("");
    await qc.invalidateQueries({ queryKey: ["settings"] });
    await qc.invalidateQueries({ queryKey: ["audit"] });
  }

  if (!settings.data) return <Skeleton className="h-48 w-full max-w-2xl" />;
  return (
    <>
      <PageHeader title={t("nav.settings")} description={t("settings.description")} />
      <div className="flex max-w-2xl flex-col gap-4">
        {!editable && <Alert variant="info">{t("settings.readOnly")}</Alert>}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between gap-4">
              {t("settings.registrations")}
              <Switch checked={registrationsOpen} disabled={!editable} onCheckedChange={setRegistrationsOpen} aria-label={t("settings.registrations")} />
            </CardTitle>
            <CardDescription>{t("settings.registrationsHint")}</CardDescription>
          </CardHeader>
          {editable && registrationsOpen !== settings.data.registrationsOpen && (
            <CardContent>
              <Button onClick={() => void save("registrationsOpen", registrationsOpen)}>{t("action.save")}</Button>
            </CardContent>
          )}
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between gap-4">
              {t("settings.maintenance")}
              <Switch
                checked={maintenance.on}
                disabled={!editable}
                onCheckedChange={(on) => setMaintenance((m) => ({ ...m, on }))}
                aria-label={t("settings.maintenance")}
              />
            </CardTitle>
            <CardDescription>{t("settings.maintenanceHint")}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {contentLangs().map((l) => (
              <div key={l} className="flex flex-col gap-1.5">
                <Label htmlFor={`msg-${l}`}>{t(MESSAGE_KEY[l])}</Label>
                <Textarea
                  id={`msg-${l}`}
                  maxLength={300}
                  disabled={!editable}
                  value={maintenance.message[l]}
                  onChange={(e) => setMaintenance((m) => ({ ...m, message: { ...m.message, [l]: e.target.value } }))}
                  className="min-h-16"
                />
              </div>
            ))}
            {editable && JSON.stringify(maintenance) !== JSON.stringify(settings.data.maintenance) && (
              <Button className="self-start" onClick={() => void save("maintenance", maintenance)}>
                {t("action.save")}
              </Button>
            )}
          </CardContent>
        </Card>
        {editable && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="reason">{t("settings.reason")}</Label>
            <Input id="reason" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
        )}
      </div>
    </>
  );
}
